import {createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable} from '@tanstack/react-table';
import {useCallback, useMemo, useState} from 'react';
import MaintenanceRealizeForm from './realize-form';
import type MaintenanceModel from 'src/core/models/maintenanceModel';
import SearchInput from 'src/autobahn/components/search-input';
import styles from './upcoming-tab.module.scss';
import sharedStyles from 'src/autobahn/shared/styles.module.scss';
import Table from 'src/autobahn/components/table';
import IconButton from 'src/autobahn/components/button/icon-button';
import {useMaintenances, useMaintenancesRealizedByStatus} from './queries';
import {useTablePagination} from 'src/autobahn/hooks/useTablePagination';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import {formatDate} from 'src/helpers/date';
import useTimezone from 'src/hooks/use-timezone';
import {useIntl} from 'react-intl';
import {formatMeasure} from './format-measure';


type UpcomingMaintenance = {
  id: number;
  deviceId: number;
  maintenanceId: number;
  status: 'pending' | 'inProgress' | 'completed';
  vehicle: string; // {plate} - {model} e.g. "ABC-1234 - Corolla"
  title: string; // maintenance title
  kind: {
    existsOdometer: boolean;
    existsDate: boolean;
    existsHourmeter: boolean;
  },
  period: { // current values
    hourmeterValue?: number; // e.g. 1000
    odometerValue?: number; // e.g. 50000
    dateValue?: string; // e.g. "2023-10-01"
  };
  latest: { // last realized values
    odometerValue?: number; // e.g. 48000
    hourmeterValue?: number; // e.g. 900
    dateValue?: string; // e.g. "2023-09-01"
  },
  next: { // the values on maintenance plan
    odometerValue?: number; // e.g. 52000
    hourmeterValue?: number; // e.g. 1100
    dateValue?: string; // e.g. "2023-11-01"
  };
  current: {
    odometerValue?: number; // e.g. 50000
    hourmeterValue?: number; // e.g. 1100
    dateValue?: string; // e.g. "2023-11-01"
  },
}

const columnHelper = createColumnHelper<UpcomingMaintenance>();

const MaintenanceUpcomingTab = () => {
  const [isMaintenanceRealizeFormOpen, setMaintenanceRealizeFormOpen] = useState(false);
  const [selectedMaintenance, selectMaintenance] = useState<MaintenanceModel | null>(null);
  const [filterText, setFilterText] = useState('');

  const {timezone} = useTimezone();

  const intl = useIntl();

  const {data: maintenances} = useMaintenances();
  const {data: pendingMaintenances} = useMaintenancesRealizedByStatus('pending');

  const data = useMemo(() => pendingMaintenances.reduce((accu, pendingMaintenance) => {
    const currentMaintenancePlan = maintenances.find((maintenances) => maintenances.id === pendingMaintenance.maintenanceId);

    if (!currentMaintenancePlan) {
      return accu;
    }

    accu.push({
      id: pendingMaintenance.id as number,
      deviceId: pendingMaintenance.deviceId!,
      maintenanceId: currentMaintenancePlan.id,
      status: 'pending',
      vehicle: pendingMaintenance.vehicle!,
      title: currentMaintenancePlan.name,
      kind: {
        existsOdometer: !!currentMaintenancePlan.odometerValue,
        existsDate: !!currentMaintenancePlan.dateValue,
        existsHourmeter: !!currentMaintenancePlan.hourmeterValue,
      },
      period: {
        odometerValue: currentMaintenancePlan.odometerValue,
        hourmeterValue: currentMaintenancePlan.hourmeterValue,
        dateValue: currentMaintenancePlan.dateValue ? formatDate(currentMaintenancePlan.dateValue, intl.formatMessage({id: 'dateFormat'}), timezone) : '-',
      },
      latest: {
        odometerValue: pendingMaintenance.lastOdometer,
        hourmeterValue: pendingMaintenance.lastHourmeter,
        dateValue: pendingMaintenance.lastDate ? formatDate(pendingMaintenance.lastDate, intl.formatMessage({id: 'dateFormat'}), timezone) : '-',
      },
      next: {
        odometerValue: pendingMaintenance.nextOdometer,
        hourmeterValue: pendingMaintenance.nextHourmeter,
        dateValue: pendingMaintenance.date ? formatDate(pendingMaintenance.date, intl.formatMessage({id: 'dateFormat'}), timezone) : '-',
      },
      current: {
        odometerValue: pendingMaintenance.currentOdometer,
        hourmeterValue: pendingMaintenance.currentHourmeter,
        dateValue: pendingMaintenance.currentDate ? formatDate(pendingMaintenance.currentDate, intl.formatMessage({id: 'dateFormat'}), timezone) : '-',
      },
    });

    return accu;
  }, [] as UpcomingMaintenance[]), [maintenances, pendingMaintenances, timezone]);

  const openMaintenanceRealizeForm = useCallback((pendingMaintenanceId: number) => {
    const maintenance = pendingMaintenances.find((m) => m.id === pendingMaintenanceId);
    selectMaintenance(maintenance!);
    setMaintenanceRealizeFormOpen(true);
  }, [pendingMaintenances]);

  const closeMaintenanceRealizeForm = () => {
    setMaintenanceRealizeFormOpen(false);
    selectMaintenance(null);
  };

  const columns = useMemo(() => [
    columnHelper.display({
      // header: 'vehicle',
      id: 'vehicle',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            {/* 'YH2045 - Uno' */}
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedVehicle'})}</span>
            <span className={styles.rowDescription}>{row.original.vehicle}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'title',
      id: 'title',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedTitle'})}</span>
            <span className={styles.rowDescription}>{row.original.title}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'Tipo',
      id: 'kind',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedKind'})}</span>
            {!!rowValue.kind.existsOdometer && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedOdometer'})}</span>}
            {!!rowValue.kind.existsDate && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedDate'})}</span>}
            {!!rowValue.kind.existsHourmeter && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedHourmeter'})}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'period',
      id: 'period',
      // enableHiding: true,
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedPeriod'})}</span>
            {!!rowValue.period.odometerValue && <span className={styles.rowDescription}>{formatMeasure(rowValue.period.odometerValue)}</span>}
            {!!rowValue.period.dateValue && <span className={styles.rowDescription}>{rowValue.period.dateValue}</span>}
            {!!rowValue.period.hourmeterValue && <span className={styles.rowDescription}>{formatMeasure(rowValue.period.hourmeterValue)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'latest',
      id: 'latest',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedLatest'})}</span>
            {!!rowValue.latest.odometerValue && <span className={styles.rowDescription}>{formatMeasure(rowValue.latest.odometerValue)}</span>}
            {!!rowValue.latest.dateValue && <span className={styles.rowDescription}>{rowValue.latest.dateValue}</span>}
            {!!rowValue.latest.hourmeterValue && <span>{formatMeasure(rowValue.latest.hourmeterValue)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'next',
      id: 'next',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedNextF'})}</span>
            {!!rowValue.next.odometerValue && <span className={styles.rowDescription}>{formatMeasure(rowValue.next.odometerValue)}</span>}
            {!!rowValue.next.dateValue && <span className={styles.rowDescription}>{rowValue.next.dateValue}</span>}
            {!!rowValue.next.hourmeterValue && <span>{formatMeasure(rowValue.next.hourmeterValue)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      // header: 'Atual',
      id: 'current',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedCurrent'})}</span>
            {!!rowValue.current.odometerValue && <span className={styles.rowDescription}>{formatMeasure(rowValue.current.odometerValue)}</span>}
            {!!rowValue.current.dateValue && <span>{rowValue.current.dateValue}</span>}
            {!!rowValue.current.hourmeterValue && <span>{formatMeasure(rowValue.current.hourmeterValue)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'action',
      cell: ({row}) => (
        <div className={sharedStyles.actionButtons}>
          <IconButton label={intl.formatMessage({id: 'sharedEdit'})} className={sharedStyles.actionButton} onClick={() => openMaintenanceRealizeForm(row.original.id)}>
            <i className='far fa-file-check' />
          </IconButton>
        </div>
      ),
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
  ], [openMaintenanceRealizeForm]);

  const {pagination, setPagination, currentPageSize} = useTablePagination();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilterText,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: 'includesString',
    state: {
      pagination: {
        ...pagination,
        pageSize: currentPageSize,
      },
      globalFilter: filterText,
    },
  });

  if (isMaintenanceRealizeFormOpen && selectedMaintenance) {
    return (
      <MaintenanceRealizeForm
        selectedMaintenance={selectedMaintenance}
        onCancel={closeMaintenanceRealizeForm}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <SearchInput onChange={table.setGlobalFilter} />
      </div>

      <Table table={table} hideHeaders emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})} />

      <TablePageControls
        pageCount={table.getPageCount()}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        onPageSizeChange={(newPageSize) => {
          if (newPageSize === -1) {
            table.setPageSize(data.length);
            setPagination((prev) => ({...prev, pageSize: -1}));
          } else {
            table.setPageSize(newPageSize);
            setPagination((prev) => ({...prev, pageSize: newPageSize}));
          }
        }}
        setPageIndex={table.setPageIndex}
        getCanPreviousPage={table.getCanPreviousPage}
        getCanNextPage={table.getCanNextPage}
        previousPage={table.previousPage}
        nextPage={table.nextPage}
      />
    </div>
  );
};

export default MaintenanceUpcomingTab;
